import { useState, useEffect, SetStateAction, Dispatch } from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import {
    fetchCountries,
    fetchLocalities,
    fetchRegions,
    fetchStates,
    selectLocationSearch,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { ILocation, LocationSelections, sectionConfigs, SectionKey } from "@interfaces";

interface LocationPickerProps {
    setSelections: Dispatch<SetStateAction<LocationSelections>>;
    selections: LocationSelections;
}

export const LocationPicker = ({ setSelections, selections }: LocationPickerProps) => {
    const dispatch = useAppDispatch();
    const searchState = useAppSelector(selectLocationSearch);
    const [expanded, setExpanded] = useState<SectionKey | null>(null);

    // initial load for countries
    useEffect(() => {
        if (_.isEmpty(searchState.country)) {
            dispatch(fetchCountries());
        }
    }, [dispatch, searchState.country]);

    useEffect(() => {
        const countryId = selections.Country?.id;
        if (countryId) dispatch(fetchStates(countryId));
    }, [dispatch, selections.Country?.id]);

    useEffect(() => {
        const { State, Country } = selections;
        dispatch(
            fetchRegions({
                stateId: State?.id,
                countryId: Country?.id,
            }),
        );
    }, [dispatch, selections.State?.id, selections.Country?.id]);

    useEffect(() => {
        const { Region, Country } = selections;
        dispatch(
            fetchLocalities({
                regionId: Region?.id,
                countryId: Country?.id,
            }),
        );
    }, [dispatch, selections.Region?.id, selections.Country?.id]);

    const toggle = (section: SectionKey) =>
        setExpanded((prev) => (prev === section ? null : section));

    const handleSelect = (section: SectionKey, item: ILocation | null) => {
        const config = sectionConfigs.find((sc) => sc.key === section)!;
        const resetValues = _.fromPairs(_.map(config.resetAfter, (k) => [k, null]));
        setSelections((prev) => ({
            ...prev,
            [section]: item,
            ...resetValues,
        }));
    };

    return (
        <PickerWrapper>
            <Body>
                {_.map(sectionConfigs, ({ key, loadingKey, errorKey }) => {
                    const items: ILocation[] = _.get(searchState, key.toLowerCase(), []);
                    const isLoading = _.get(searchState, loadingKey, false);
                    const error = _.get(searchState, errorKey, null);

                    // build displayItems with “Any” for those three sections
                    const withAny = _.includes(["State", "Region", "Locality"], key)
                        ? _.concat([{ id: "", text: "Any", isAny: true }], items)
                        : items;
                    const displayItems = _.filter(withAny, (it) =>
                        it.isAny
                            ? true
                            : !_.includes(it.text, "- To be Confirmed") &&
                              !_.includes(it.text, "No Results"),
                    );

                    // if nothing selected, defaultText is “Any” for State/Region/Locality, else blank
                    const defaultText = _.includes(["State", "Region", "Locality"], key)
                        ? "Any"
                        : "";
                    // get the actual display label: either the selected item's .text, or defaultText
                    const selectedText = _.get(selections, [key, "text"], defaultText);

                    return (
                        <div key={key}>
                            <SectionHeader onClick={() => toggle(key)}>
                                <HeaderLabel>
                                    <PinIcon />
                                    {`Choose ${key}`}
                                </HeaderLabel>
                                <Chevron
                                    style={{
                                        transform:
                                            expanded === key ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                />
                            </SectionHeader>

                            {/* show “Any” by default when nothing is picked */}
                            <SectionSelected onClick={() => toggle(key)}>
                                {selectedText}
                            </SectionSelected>

                            {expanded === key && (
                                <Options>
                                    {isLoading ? (
                                        <LoadingText>Loading {_.toLower(key)}…</LoadingText>
                                    ) : error ? (
                                        <LoadingText>Error loading {_.toLower(key)}</LoadingText>
                                    ) : _.isEmpty(displayItems) ? null : (
                                        _.map(displayItems, (item) => {
                                            const isAny = Boolean((item as any).isAny);
                                            // “Any” is selected when selections[key] is null
                                            const selected = isAny
                                                ? _.isNil(selections[key])
                                                : selections[key]?.id === item.id;

                                            return (
                                                <OptionRow
                                                    key={isAny ? "any" : item.id}
                                                    onClick={() =>
                                                        handleSelect(
                                                            key,
                                                            isAny ? null : (item as ILocation),
                                                        )
                                                    }
                                                >
                                                    <RadioCircle selected={selected} />
                                                    <OptionLabel selected={selected}>
                                                        {item.text}
                                                    </OptionLabel>
                                                </OptionRow>
                                            );
                                        })
                                    )}
                                </Options>
                            )}
                        </div>
                    );
                })}
            </Body>
        </PickerWrapper>
    );
};

/** Styled Components **/

const PickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: auto;
`;

const Body = styled.div`
    overflow-y: auto;
    padding: 18px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding-top: 20px;
`;

const SectionSelected = styled.div`
    border-bottom: 1px solid #e0e0e0;
    padding: 5px 0 10px 40px;
    color: #31b4b9;
`;

const HeaderLabel = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 700;
    color: #333;
    gap: 20px;
`;

const PinIcon = () => <img src="/images/fi_map-pin.png" alt="pin" width={20} height={20} />;

const Chevron = styled.img.attrs({
    src: "/images/fi_chevron-down.png",
    alt: "chevron",
})`
    width: 20px;
    height: 20px;
    transition: transform 0.2s;
`;

const Options = styled.div`
    padding: 8px 0 0px 32px;
`;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    gap: 16px;
    padding: 8px 0;
    cursor: pointer;
`;

const RadioCircle = styled.div<{ selected: boolean }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid ${({ selected }) => (selected ? "#31B4B9" : "#daddde")};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.3s ease;

    ${({ selected }) =>
        selected &&
        css`
            background-color: #31b4b9;

            &::after {
                content: "";
                width: 10px;
                height: 10px;
                background-color: #fff;
                border-radius: 50%;
            }
        `}
`;

const OptionLabel = styled.span<{ selected: boolean }>`
    flex-grow: 1;
    font-size: 16px;
    color: ${({ selected }) => (selected ? "#007aff" : "#555")};
`;

const LoadingText = styled.div`
    padding: 8px 0 0px 8px;
    color: #888;
`;
